import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';

import type { FilterField, FilterSet } from '@/shared/filters/define-filters';
import { filterAction } from '@/shared/filters/filter-action';
import { isCivilDate } from '@/shared/format/date';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen } from '@/shared/screen/define';
import type { ScreenContext } from '@/shared/screen/types';

import { INCONSISTENCY_FILTERS, NOT_FINISHED_FILTERS, TRANSFER_LINE_FILTERS } from './conciliation.filters';
import { CONCILIATION_PATH, DATAPREV_PATH, NEW_CONCILIATION_PATH, SUSPENSE_ACCOUNT_PATH, conciliationPath } from './conciliation.paths';
import { DETAIL_TABS } from './conciliation.search';

// Manifestos da Conciliação Dataprev (U23, U42). Arquivo leve: entra no `src/app/screens.ts` e no ⌘K.
// Rota com `isRootTenancy` e `Conciliation:Read` (router.tsx:1455-1462), herdados por todas as telas.
// Nenhuma ação de escrita tem gate no front no legado (tratativas, lote, concluir, desvincular): a recusa
// vem do backend (discovery/10, módulo conciliation; pergunta no relatório).

export const CONCILIATION_READ_PERMISSION = can('Conciliation', 'Read');
const ACCESS = { rootTenancy: true } as const;

const emptyInput = z.strictObject({});
const lineInput = z.strictObject({ id: z.string().min(1) });

/** Ação que só existe com a tela montada: a tela troca o `run` pelo dela. */
const mountedScreenRequired = (): never => {
  throw new Error(t`Abra a tela da conciliação para usar esta ação.`);
};

const sheetPreview = () => ({
  title: t`Abrir o formulário na tela`,
  detail: t`Confira os dados e confirme na tela. Nada é gravado sem a confirmação da pessoa.`,
});

// ─── Hub ─────────────────────────────────────────────────────────────────────────────────────────

export const conciliationOpenDataprev = action({
  id: 'conciliation.openDataprev',
  kind: 'navigate',
  label: msg`Dataprev`,
  description: msg`Abre a lista de conciliações Dataprev, o único tipo de conciliação disponível.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(DATAPREV_PATH),
});

export const conciliationHubScreen = defineScreen({
  id: 'conciliation.hub',
  path: CONCILIATION_PATH,
  title: msg`Conciliação`,
  description: msg`Entrada da conciliação de repasses da carteira. Hoje só existe a conciliação Dataprev, então a tela abre direto a lista Dataprev.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  place: { icon: 'receipt-text', section: 'wallet-management' },
  actions: [conciliationOpenDataprev],
});

// ─── Lista Dataprev ──────────────────────────────────────────────────────────────────────────────

export const conciliationPage = listPageAction('conciliation.page');

export const conciliationOpen = action<{ id: string }>({
  id: 'conciliation.open',
  kind: 'navigate',
  label: msg`Abrir conciliação`,
  description: msg`Abre os detalhes de uma conciliação da lista: números, inconsistências, linhas e histórico. Nada muda só por abrir.`,
  input: z.object({ id: z.string().min(1) }),
  run: ({ nav, input }) => nav.to(conciliationPath(input.id)),
});

export const conciliationNew = action({
  id: 'conciliation.new',
  kind: 'navigate',
  label: msg`Nova conciliação`,
  description: msg`Abre a tela de nova conciliação, onde se define o período, consulta os repasses da Dataprev e gera o processo.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(NEW_CONCILIATION_PATH),
});

export const conciliationSuspenseAccount = action({
  id: 'conciliation.suspenseAccount',
  kind: 'navigate',
  label: msg`Bolsão`,
  description: msg`Abre as contas Bolsão das operações, com o saldo atual e o extrato de cada uma.`,
  input: emptyInput,
  run: ({ nav }) => nav.to(SUSPENSE_ACCOUNT_PATH),
});

export const conciliationListScreen = defineScreen({
  id: 'conciliation.dataprev',
  path: DATAPREV_PATH,
  title: msg`Dataprev`,
  description: msg`Lista das conciliações Dataprev, com período, valor realizado, inconsistências, inadimplências e status, mais os atalhos para o Bolsão e para uma nova conciliação.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  place: { icon: 'receipt-text', parent: 'conciliation.hub' },
  actions: [conciliationOpen, conciliationPage, conciliationNew, conciliationSuspenseAccount],
});

// ─── Nova conciliação ────────────────────────────────────────────────────────────────────────────

const civilDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).refine(isCivilDate);
export const queryInput = z.strictObject({ startDate: civilDate, endDate: civilDate, bookkeepingStartDate: civilDate, bookkeepingEndDate: civilDate });

export const conciliationQuery = action<z.infer<typeof queryInput>>({
  id: 'conciliation.query',
  kind: 'navigate',
  label: msg`Consultar`,
  description: msg`Consulta os repasses da Dataprev no período de conciliação e guarda o período de escrituração para gerar o processo. Datas no formato AAAA-MM-DD; o fim precisa ser depois do início e não pode passar de hoje.`,
  input: queryInput,
  run: mountedScreenRequired,
});

export const conciliationPreviewPage = listPageAction('conciliation.previewPage');

export const conciliationGenerate = action({
  id: 'conciliation.generate',
  kind: 'write',
  label: msg`Gerar processo`,
  description: msg`Abre "Gerar processo" para o período consultado, onde a pessoa informa as datas de liquidação e de baixa. Nada é gerado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: sheetPreview,
  run: ({ nav }) => nav.search({ sheet: 'gerar' }),
});

export const conciliationNewScreen = defineScreen({
  id: 'conciliation.newProcess',
  path: NEW_CONCILIATION_PATH,
  title: msg`Nova conciliação`,
  description: msg`Nova conciliação Dataprev: define o período de conciliação e o de escrituração, consulta os repasses do período e gera o processo com as datas de liquidação e de baixa.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  place: { icon: 'file-text', parent: 'conciliation.dataprev' },
  actions: [conciliationQuery, conciliationPreviewPage, conciliationGenerate],
});

// ─── Detalhe ─────────────────────────────────────────────────────────────────────────────────────

export const conciliationRefresh = action({
  id: 'conciliation.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os números e o histórico da conciliação aberta, com a carteira escolhida.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const conciliationComplete = action({
  id: 'conciliation.complete',
  kind: 'write',
  label: msg`Concluir sem financeiro`,
  description: msg`Conclui a conciliação aberta sem gerar financeiro. Só aparece quando a conciliação está aguardando conclusão.`,
  input: emptyInput,
  preview: () => ({ title: t`Concluir sem financeiro`, detail: t`A conciliação aberta na tela será concluída sem gerar financeiro.` }),
  run: mountedScreenRequired,
});

export const conciliationReports = action({
  id: 'conciliation.reports',
  kind: 'draft',
  label: msg`Relatórios da conciliação`,
  description: msg`Abre a gaveta de relatórios da conciliação aberta, para escolher e gerar um relatório. Só aparece a partir da etapa de conclusão.`,
  input: emptyInput,
  run: ({ nav }) => nav.search({ sheet: 'relatorios' }),
});

export const walletInput = z.strictObject({ walletCode: z.number().int().min(-1).nullable() });

export const conciliationWallet = action<z.infer<typeof walletInput>>({
  id: 'conciliation.wallet',
  kind: 'navigate',
  label: msg`Selecione a carteira`,
  description: msg`Filtra a conciliação aberta por carteira (código do convênio). -1 mostra só as linhas sem carteira; null volta a todas. O resumo por fundo não muda.`,
  input: walletInput,
  run: ({ nav, input }) => nav.search({ walletCode: input.walletCode ?? undefined, page: undefined, selected: undefined, sheet: undefined, line: undefined }),
});

export const conciliationLinesPage = listPageAction('conciliation.linesPage');

/** Filtrar a aba: cada aba tem os próprios filtros (o agente só recebe o da aba aberta). */
const tabFilter = <Fs extends readonly FilterField[]>(id: string, set: FilterSet<Fs>, tab: string) => {
  const base = filterAction(id, set);
  return { ...base, run: ({ nav, input }: Parameters<typeof base.run>[0]) => nav.search({ ...set.patch(input), tab, selected: undefined }) } as typeof base;
};
export const conciliationInconsistencyFilter = tabFilter('conciliation.inconsistencyFilter', INCONSISTENCY_FILTERS, 'inconsistencias');
export const conciliationNotFinishedFilter = tabFilter('conciliation.notFinishedFilter', NOT_FINISHED_FILTERS, 'nao-encerradas');
export const conciliationTransferLineFilter = tabFilter('conciliation.transferLineFilter', TRANSFER_LINE_FILTERS, 'linha-de-repasse');

export const conciliationSelect = action<{ ids: string[] }>({
  id: 'conciliation.select',
  kind: 'draft',
  label: msg`Selecionar linhas`,
  description: msg`Marca linhas da página atual da aba Inconsistências, para editar a tratativa em lote ou tratar via Bolsão em lote. Linha já tratada e sem ação disponível não pode ser marcada.`,
  input: z.object({ ids: z.array(z.string()) }),
  run: ({ nav, input }) => nav.search({ selected: input.ids.length > 0 ? input.ids : undefined, sheet: undefined, line: undefined }),
});

export const conciliationBatchEdit = action({
  id: 'conciliation.batchEdit',
  kind: 'write',
  label: msg`Editar trativa em lote`,
  description: msg`Abre a tratativa em lote para as linhas marcadas na aba Inconsistências que ainda não têm tratativa. Nada é gravado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: sheetPreview,
  run: ({ nav }) => nav.search({ sheet: 'lote', line: undefined }),
});

export const conciliationBatchSuspense = action({
  id: 'conciliation.batchSuspense',
  kind: 'write',
  label: msg`Bolsão em lote`,
  description: msg`Abre a tratativa via Bolsão em lote para as linhas marcadas na aba Inconsistências, com a prévia do que cada uma consome do saldo. Nada é aplicado sem a confirmação da pessoa.`,
  input: emptyInput,
  preview: sheetPreview,
  run: ({ nav }) => nav.search({ sheet: 'bolsao-lote', line: undefined }),
});

export const conciliationExport = action({
  id: 'conciliation.export',
  kind: 'read',
  label: msg`Exportar CSV`,
  description: msg`Exporta em CSV as linhas da aba aberta (Inconsistências, Não encerradas ou Linha de repasse) com os filtros aplicados. Arquivo grande vai por e-mail.`,
  input: emptyInput,
  run: mountedScreenRequired,
});

export const conciliationLineTreatment = action<{ id: string }>({
  id: 'conciliation.lineTreatment',
  kind: 'draft',
  label: msg`Tratativa`,
  description: msg`Abre a tratativa de uma linha da aba Inconsistências ainda sem tratativa, pelo id da linha na página atual. Nada é gravado sem a confirmação da pessoa.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationLineSuspense = action<{ id: string }>({
  id: 'conciliation.lineSuspense',
  kind: 'draft',
  label: msg`Bolsão`,
  description: msg`Abre a tratativa via Bolsão de uma linha da aba Inconsistências que tem ação disponível, com a prévia do saldo. Nada é aplicado sem a confirmação da pessoa.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationLineTransferTreatment = action<{ id: string }>({
  id: 'conciliation.lineTransferTreatment',
  kind: 'draft',
  label: msg`Tratativa da linha de repasse`,
  description: msg`Abre a tratativa de uma linha da aba Linha de repasse que tem ação disponível: via Bolsão ou Aplicar juros e multa, conforme a ação sugerida. Nada é aplicado sem a confirmação da pessoa.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationLineUnlink = action<{ id: string }>({
  id: 'conciliation.lineUnlink',
  kind: 'draft',
  label: msg`Desvincular parcelamento`,
  description: msg`Abre o desvincular parcelamento de uma linha da aba Linha de repasse, enquanto a conclusão não começou. Nada é desfeito sem a confirmação da pessoa.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationLineObservations = action<{ id: string }>({
  id: 'conciliation.lineObservations',
  kind: 'read',
  label: msg`Observações`,
  description: msg`Mostra o resumo de uma linha da aba aberta: repasse, Bolsão, situação e classificação.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationLineInstallment = action<{ id: string }>({
  id: 'conciliation.lineInstallment',
  kind: 'navigate',
  label: msg`Parcelamento`,
  description: msg`Abre o parcelamento vinculado a uma linha da aba aberta, quando a linha tem parcelamento.`,
  input: lineInput,
  run: mountedScreenRequired,
});

export const conciliationDetailScreen = defineScreen({
  id: 'conciliation.detail',
  path: `${DATAPREV_PATH}/$conciliationId`,
  title: msg`Detalhes da conciliação`,
  description: msg`Detalhe de uma conciliação Dataprev: etapas do processo, números gerais, resumo por fundo, as linhas inconsistentes com as tratativas (individual, em lote e via Bolsão), as não encerradas, a linha de repasse e o histórico.`,
  permission: CONCILIATION_READ_PERMISSION,
  access: ACCESS,
  place: { icon: 'file-text', parent: 'conciliation.dataprev', record: msg`Conciliação`, tabs: DETAIL_TABS },
  actions: [
    conciliationRefresh,
    conciliationComplete,
    conciliationReports,
    conciliationWallet,
    conciliationLinesPage,
    conciliationInconsistencyFilter,
    conciliationNotFinishedFilter,
    conciliationTransferLineFilter,
    conciliationSelect,
    conciliationBatchEdit,
    conciliationBatchSuspense,
    conciliationExport,
    conciliationLineTreatment,
    conciliationLineSuspense,
    conciliationLineTransferTreatment,
    conciliationLineUnlink,
    conciliationLineObservations,
    conciliationLineInstallment,
  ],
});

// ─── Contexto para o agente (U24, U46 D2-C): o que a pessoa vê, sem CPF nem contrato ──────────────

export function listContext(input: { readonly page: number; readonly total: number | undefined }): ScreenContext {
  const { page, total } = input;
  return {
    summary: [t`Conciliações Dataprev, página ${page}.`, total === undefined ? t`Carregando.` : t`Conciliações encontradas: ${total}.`].join(' '),
    fields: { page, total: total ?? null },
    sensitivity: 'internal',
  };
}

export function newContext(input: { readonly consulted: boolean; readonly total: number | undefined; readonly sheet: boolean }): ScreenContext {
  const parts = [t`Nova conciliação Dataprev.`];
  if (!input.consulted) parts.push(t`Nenhum período consultado ainda.`);
  else if (input.total === undefined) parts.push(t`Consultando os repasses do período.`);
  else {
    const total = input.total;
    parts.push(t`Repasses encontrados no período: ${total}.`);
  }
  if (input.sheet) parts.push(t`Gaveta "Gerar processo" aberta.`);
  return { summary: parts.join(' '), fields: { consulted: input.consulted, total: input.total ?? null, generateOpen: input.sheet }, sensitivity: 'internal' };
}

export function detailContext(input: {
  readonly id: string;
  readonly loaded: boolean;
  readonly status: string | undefined;
  readonly step: string;
  readonly tab: string;
  readonly walletCode: number | undefined;
  readonly lines: number | undefined;
  readonly selected: number;
}): ScreenContext {
  if (!input.loaded) return { summary: t`Conciliação carregando.`, entity: { type: 'Conciliation', id: input.id }, fields: {}, sensitivity: 'internal' };
  const { step, tab, selected } = input;
  const wallet = input.walletCode === undefined ? t`Todas as carteiras.` : input.walletCode === -1 ? t`Só linhas sem carteira.` : t`Carteira ${input.walletCode}.`;
  const lines = input.lines;
  return {
    summary: [
      t`Conciliação aberta na etapa ${step}, aba ${tab}.`,
      wallet,
      lines === undefined ? '' : t`Linhas na aba: ${lines}.`,
      selected > 0 ? t`Linhas marcadas: ${selected}.` : '',
    ]
      .filter(Boolean)
      .join(' '),
    entity: { type: 'Conciliation', id: input.id },
    fields: { status: input.status ?? null, tab: input.tab, walletCode: input.walletCode ?? null, lines: lines ?? null, selectedCount: selected },
    sensitivity: 'internal',
  };
}
